import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { LandingHeader } from '@/components/landing/LandingHeader'
import { HeroSection } from '@/components/landing/HeroSection'
import { FeaturesSection } from '@/components/landing/FeaturesSection'
import { HowItWorksSection } from '@/components/landing/HowItWorksSection'
import { LandingFooter } from '@/components/landing/LandingFooter'

const LEFT_NETWORK_PATHS = [
  'M48 -6L92 18L76 58',
  'M92 18L134 46L110 94',
  'M134 46L182 24L228 44L228 96',
  'M76 58L110 94L64 132L38 196L96 228L144 278L104 352',
  'M110 94L152 82L190 124L132 164L96 228',
  'M132 164L168 210L210 272L176 338L104 352',
  'M64 132L132 164L144 278L74 298L28 332',
  'M152 82L228 96L244 148L190 124',
  'M168 210L222 186L244 148L210 272',
  'M48 268L96 228L74 298L28 332',
  'M176 338L212 396L144 424L84 392L104 352',
  'M244 148L276 210L222 186',
  'M210 272L260 302L212 396',
  'M212 396L236 456L182 496L116 482L84 392',
  'M104 352L64 430L116 482',
  'M228 96L286 74L318 126L244 148',
  'M276 210L318 126L344 198L296 254L260 302',
  'M296 254L338 332L290 406L236 456',
  'M318 126L374 94L402 150L344 198',
  'M38 196L-6 248L48 268',
  'M338 332L388 286L430 346L366 408L290 406',
  'M236 456L272 512L206 538L142 520L116 482',
] as const

const LEFT_NETWORK_NODES = [
  { x: 48, y: -6, r: 0.9 },
  { x: 92, y: 18, r: 1.0 },
  { x: 134, y: 46, r: 1.05 },
  { x: 182, y: 24, r: 0.9 },
  { x: 228, y: 44, r: 0.9 },
  { x: 76, y: 58, r: 1.35 },
  { x: 110, y: 94, r: 1.45 },
  { x: 152, y: 82, r: 1.25 },
  { x: 190, y: 124, r: 1.2 },
  { x: 228, y: 96, r: 1.1 },
  { x: 64, y: 132, r: 1.3 },
  { x: 132, y: 164, r: 1.5 },
  { x: 244, y: 148, r: 1.15 },
  { x: 38, y: 196, r: 1.1 },
  { x: 96, y: 228, r: 1.3 },
  { x: 168, y: 210, r: 1.25 },
  { x: 222, y: 186, r: 1.05 },
  { x: 276, y: 210, r: 1.0 },
  { x: 48, y: 268, r: 1.0 },
  { x: 144, y: 278, r: 1.35 },
  { x: 210, y: 272, r: 1.15 },
  { x: 260, y: 302, r: 1.0 },
  { x: 74, y: 298, r: 1.15 },
  { x: 28, y: 332, r: 1.0 },
  { x: 104, y: 352, r: 1.3 },
  { x: 176, y: 338, r: 1.15 },
  { x: 212, y: 396, r: 1.15 },
  { x: 144, y: 424, r: 1.05 },
  { x: 84, y: 392, r: 1.0 },
  { x: 64, y: 430, r: 1.0 },
  { x: 116, y: 482, r: 1.05 },
  { x: 182, y: 496, r: 0.95 },
  { x: 236, y: 456, r: 1.0 },
  { x: 286, y: 74, r: 0.95 },
  { x: 318, y: 126, r: 1.0 },
  { x: 374, y: 94, r: 0.9 },
  { x: 402, y: 150, r: 0.95 },
  { x: 344, y: 198, r: 0.95 },
  { x: 296, y: 254, r: 1.0 },
  { x: -6, y: 248, r: 0.8 },
  { x: 388, y: 286, r: 0.95 },
  { x: 338, y: 332, r: 1.0 },
  { x: 430, y: 346, r: 0.9 },
  { x: 366, y: 408, r: 0.95 },
  { x: 290, y: 406, r: 1.0 },
  { x: 272, y: 512, r: 0.9 },
  { x: 206, y: 538, r: 0.8 },
  { x: 142, y: 520, r: 0.8 },
] as const

const RIGHT_NETWORK_PATHS = [
  'M256 -4L308 18L362 34',
  'M308 18L362 34L406 72',
  'M288 68L346 98L372 152L316 178L282 226L236 270',
  'M406 72L458 38L512 64L500 88',
  'M346 98L406 72L454 118L470 176L420 220L346 248L282 226',
  'M316 178L372 152L420 220L404 270L368 330L304 316L262 374',
  'M346 248L404 270L442 312L480 354L420 398L336 404L262 374',
  'M470 176L486 258L480 354',
  'M304 316L346 248L368 330',
  'M420 220L454 118L500 88',
  'M336 404L294 458L392 470L420 398',
  'M236 270L210 334L262 374',
  'M372 152L438 142L470 176',
  'M480 354L502 432L446 486L392 470',
  'M336 404L308 486L246 502L214 452L262 374',
  'M454 118L500 88L514 154L470 176',
  'M404 270L454 236L486 258L442 312',
  'M288 68L246 132L282 226L236 270',
  'M438 142L488 126L514 154',
  'M486 258L530 308L480 354',
  'M420 398L468 438L446 486',
  'M308 486L344 540L408 526L446 486',
  'M210 334L214 452L180 516L246 502',
  'M236 270L184 238L246 132',
] as const

const RIGHT_NETWORK_NODES = [
  { x: 256, y: -4, r: 0.85 },
  { x: 308, y: 18, r: 0.95 },
  { x: 362, y: 34, r: 1.0 },
  { x: 288, y: 68, r: 1.25 },
  { x: 346, y: 98, r: 1.45 },
  { x: 406, y: 72, r: 1.15 },
  { x: 458, y: 38, r: 0.9 },
  { x: 512, y: 64, r: 0.85 },
  { x: 454, y: 118, r: 1.25 },
  { x: 500, y: 88, r: 1.0 },
  { x: 372, y: 152, r: 1.2 },
  { x: 438, y: 142, r: 1.0 },
  { x: 488, y: 126, r: 0.9 },
  { x: 316, y: 178, r: 1.2 },
  { x: 470, y: 176, r: 1.35 },
  { x: 282, y: 226, r: 1.15 },
  { x: 184, y: 238, r: 0.9 },
  { x: 420, y: 220, r: 1.2 },
  { x: 236, y: 270, r: 1.0 },
  { x: 346, y: 248, r: 1.25 },
  { x: 404, y: 270, r: 1.05 },
  { x: 486, y: 258, r: 1.0 },
  { x: 210, y: 334, r: 1.0 },
  { x: 304, y: 316, r: 1.1 },
  { x: 368, y: 330, r: 1.25 },
  { x: 442, y: 312, r: 1.1 },
  { x: 262, y: 374, r: 1.15 },
  { x: 336, y: 404, r: 1.2 },
  { x: 420, y: 398, r: 1.1 },
  { x: 480, y: 354, r: 1.0 },
  { x: 294, y: 458, r: 1.05 },
  { x: 392, y: 470, r: 1.05 },
  { x: 468, y: 438, r: 0.95 },
  { x: 502, y: 432, r: 0.95 },
  { x: 446, y: 486, r: 1.0 },
  { x: 308, y: 486, r: 1.0 },
  { x: 344, y: 540, r: 0.8 },
  { x: 408, y: 526, r: 0.8 },
  { x: 246, y: 502, r: 0.95 },
  { x: 180, y: 516, r: 0.8 },
  { x: 214, y: 452, r: 0.95 },
  { x: 514, y: 154, r: 0.9 },
  { x: 454, y: 236, r: 0.95 },
  { x: 246, y: 132, r: 0.95 },
] as const

type LandingNetworkLayerProps = {
  className: string
  strokeId: string
  glowId: string
  paths: readonly string[]
  nodes: readonly { x: number; y: number; r: number }[]
}

function LandingNetworkLayer({ className, strokeId, glowId, paths, nodes }: LandingNetworkLayerProps) {
  return (
    <div className={`landing-network-lines ${className}`}>
      <svg viewBox="0 0 520 520" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={strokeId} x1="40" y1="40" x2="460" y2="380" gradientUnits="userSpaceOnUse">
            <stop stopColor="#A5F3FC" stopOpacity="0.72" />
            <stop offset="0.52" stopColor="#67E8F9" stopOpacity="0.44" />
            <stop offset="1" stopColor="#C084FC" stopOpacity="0.2" />
          </linearGradient>
          <radialGradient id={glowId} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(260 260) rotate(90) scale(240)">
            <stop stopColor="#22D3EE" stopOpacity="0.14" />
            <stop offset="0.55" stopColor="#7C3AED" stopOpacity="0.08" />
            <stop offset="1" stopColor="#050508" stopOpacity="0" />
          </radialGradient>
        </defs>

        <circle cx="260" cy="260" r="240" fill={`url(#${glowId})`} />

        {paths.map((path) => (
          <path key={path} d={path} stroke={`url(#${strokeId})`} strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" />
        ))}

        {nodes.map((node) => (
          <g key={`${node.x}-${node.y}`}>
            <circle cx={node.x} cy={node.y} r={node.r * 4} fill="#22D3EE" fillOpacity="0.045" />
            <circle cx={node.x} cy={node.y} r={node.r} fill="white" fillOpacity="0.82" />
          </g>
        ))}
      </svg>
    </div>
  )
}

export function LandingPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home', { replace: true })
    }
  }, [isAuthenticated, navigate])

  if (isAuthenticated) {
    return null
  }

  return (
    <div className="landing-shell min-h-screen overflow-x-hidden text-white selection:bg-cyan-400 selection:text-slate-950">
      <div aria-hidden="true" className="landing-background">
        <div className="landing-mesh-gradient" />
        <div className="landing-halftone-grid" />
        <div className="landing-background-vignette" />
        <LandingNetworkLayer
          className="landing-network-left"
          strokeId="landingNetworkStrokeLeft"
          glowId="landingNetworkGlowLeft"
          paths={LEFT_NETWORK_PATHS}
          nodes={LEFT_NETWORK_NODES}
        />
        <LandingNetworkLayer
          className="landing-network-right"
          strokeId="landingNetworkStrokeRight"
          glowId="landingNetworkGlowRight"
          paths={RIGHT_NETWORK_PATHS}
          nodes={RIGHT_NETWORK_NODES}
        />
        <div className="landing-noise-overlay" />
      </div>
      <LandingHeader />
      <main className="relative z-10">
        <HeroSection />
        <FeaturesSection />
        <HowItWorksSection />
      </main>
      <LandingFooter />
    </div>
  )
}
